import React, { useEffect, useState, useRef, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Pagination, Navigation, Zoom } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/zoom";

import styles from "./style.module.css";
import { SvgX } from "assets";
import { white } from "assets/styleUtils";

SwiperCore.use([Pagination, Navigation, Zoom]);

interface FullScreenModalProps {
  images: string[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

const FullScreenModal: React.FC<FullScreenModalProps> = ({
  images,
  initialIndex,
  isOpen,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<SwiperCore | null>(null); // Reference to Swiper instance

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        contentRef.current &&
        !contentRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (swiperRef.current && isOpen) {
      swiperRef.current.slideTo(initialIndex, 0);
    }
    setCurrentIndex(initialIndex);
  }, [initialIndex, isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, handleClickOutside]);

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.modal} ref={modalRef}>
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "5px",
            position: "fixed",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            top: "10px",
            right: "10px",
            zIndex: 1000,
          }}
        >
          <SvgX width={20} height={20} color={white} />
        </div>
        <div className={styles.modalContent} ref={contentRef}>
          <Swiper
            className={styles.swiperPagination}
            slidesPerView="auto"
            threshold={10}
            pagination={{
              dynamicBullets: true,
            }}
            navigation
            zoom={true}
            centeredSlides={true}
            initialSlide={currentIndex}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              swiper.slideTo(initialIndex, 0); // 추가된 코드
            }}
            onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
          >
            {images.map((src, index) => (
              <SwiperSlide key={index} style={{ position: "relative" }}>
                <div
                  className="swiper-zoom-container"
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <img
                    src={src}
                    alt={`img-${index}`}
                    className={styles.fullImage}
                    style={{
                      display: "block",
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </div>

                {/* 확대/축소 버튼 */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "24px",
                    right: "24px",
                    zIndex: 1000,
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      swiperRef.current?.zoom?.in();
                    }}
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      border: "none",
                      backgroundColor: "rgba(0, 0, 0, 0.6)",
                      color: "#fff",
                      fontSize: "24px",
                      cursor: "pointer",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
                    }}
                  >
                    +
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      swiperRef.current?.zoom?.out();
                    }}
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      border: "none",
                      backgroundColor: "rgba(0, 0, 0, 0.6)",
                      color: "#fff",
                      fontSize: "24px",
                      cursor: "pointer",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
                    }}
                  >
                    –
                  </button>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </>
  );
};

export default FullScreenModal;
